import { useEffect, useState } from 'react'
import { Layers, Loader2, RefreshCw } from 'lucide-react'
import { algorithmicScannerService } from '@/services/algorithmicScanner'

function pct(v) {
  if (v === null || v === undefined || Number.isNaN(v)) return '—'
  return `${(Number(v) * 100).toFixed(1)}%`
}

function num(v, digits = 2) {
  if (v === null || v === undefined || Number.isNaN(v)) return '—'
  return Number(v).toFixed(digits)
}

const INDICATOR_LABELS = {
  ict: 'ICT / SMC',
  showi_ict: 'SHOWI ICT',
  squeeze_breakout: 'Squeeze Breakout',
  smart_breakout: 'Smart Breakout',
  breakout_pattern: 'Patrón de Ruptura',
  canales_sr: 'Canales S/R',
  liquidity_sweep_pro: 'Liquidity Sweep Pro',
  supertrend_quantum: 'SuperTrend Quantum',
}

function labelFor(id) {
  return INDICATOR_LABELS[id] || id
}

function toneFor(row) {
  if (row.total_r > 0 && row.win_rate && row.win_rate >= 0.5) return 'good'
  if (row.total_r < 0 || (row.win_rate !== null && row.win_rate < 0.4)) return 'bad'
  return 'neutral'
}

const MIN_SIGNALS_OPTIONS = [1, 3, 5, 10]

export default function SignalComboRanking({ scannerId = null }) {
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [minSignals, setMinSignals] = useState(3)

  useEffect(() => {
    load(minSignals)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scannerId, minSignals])

  const load = async (min) => {
    setLoading(true)
    setError(null)
    try {
      const { data: res } = scannerId
        ? await algorithmicScannerService.getScannerComboRankings(scannerId, min)
        : await algorithmicScannerService.getGlobalComboRankings(min)
      setData(res)
    } catch (e) {
      setError(e.response?.data?.detail || 'No se pudo cargar el ranking de combinaciones')
    } finally {
      setLoading(false)
    }
  }

  const items = data?.items || []

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="section-title flex items-center gap-2">
          <Layers size={16} />
          Ranking por indicador × activo × timeframe
        </h3>
        <div className="flex items-center gap-3">
          <label className="text-[10px] text-slate-500 dark:text-gray-400 flex items-center gap-1.5">
            Mín. señales
            <select
              value={minSignals}
              onChange={(e) => setMinSignals(Number(e.target.value))}
              className="text-xs bg-slate-100 dark:bg-gray-800 border border-slate-200 dark:border-gray-700 rounded px-1.5 py-0.5"
            >
              {MIN_SIGNALS_OPTIONS.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </label>
          <button
            onClick={() => load(minSignals)}
            disabled={loading}
            className="text-[10px] text-slate-500 dark:text-gray-400 hover:text-slate-700 dark:hover:text-gray-200 flex items-center gap-1"
          >
            {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
            Refrescar
          </button>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="animate-spin text-slate-400" size={24} />
        </div>
      ) : error ? (
        <div className="flex items-center justify-between gap-3 p-3 rounded-lg bg-red-500/10 text-red-700 dark:text-red-300 text-sm border border-red-500/30">
          <span>{error}</span>
          <button onClick={() => load(minSignals)} className="text-xs underline">Reintentar</button>
        </div>
      ) : items.length === 0 ? (
        <div className="text-center py-6 text-slate-500 dark:text-gray-400 text-sm">
          <p>No hay combinaciones con al menos {minSignals} señales todavía.</p>
          <p className="text-xs mt-1">Baja el umbral de "Mín. señales" o deja correr el escáner más tiempo.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-100 dark:bg-gray-800 text-slate-600 dark:text-gray-400">
              <tr>
                <th className="text-left p-2 rounded-l-lg">#</th>
                <th className="text-left p-2">Indicador</th>
                <th className="text-left p-2">Activo</th>
                <th className="text-left p-2">TF</th>
                <th className="text-right p-2">Señales</th>
                <th className="text-right p-2">Win rate</th>
                <th className="text-right p-2">Profit factor</th>
                <th className="text-right p-2">Total R</th>
                <th className="text-right p-2 rounded-r-lg">Avg R</th>
              </tr>
            </thead>
            <tbody>
              {items.map((row, idx) => {
                const tone = toneFor(row)
                return (
                  <tr
                    key={`${row.indicator_id}-${row.symbol}-${row.timeframe}`}
                    className="border-b border-slate-200 dark:border-gray-800 hover:bg-slate-50 dark:hover:bg-gray-800/50"
                  >
                    <td className="p-2 font-bold text-slate-700 dark:text-gray-200">{idx + 1}</td>
                    <td className="p-2">
                      <span className="font-medium text-slate-900 dark:text-white">{labelFor(row.indicator_id)}</span>
                    </td>
                    <td className="p-2 font-mono text-slate-700 dark:text-gray-300">{row.symbol}</td>
                    <td className="p-2 font-mono text-slate-600 dark:text-gray-400">{row.timeframe}</td>
                    <td className="text-right p-2">{row.total_signals}</td>
                    <td className={`text-right p-2 ${tone === 'good' ? 'text-emerald-600 dark:text-emerald-400' : tone === 'bad' ? 'text-rose-600 dark:text-rose-400' : ''}`}>
                      {pct(row.win_rate)}
                    </td>
                    <td className="text-right p-2">{num(row.profit_factor)}</td>
                    <td className={`text-right p-2 font-semibold ${row.total_r >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                      {row.total_r >= 0 ? '+' : ''}{num(row.total_r)}
                    </td>
                    <td className="text-right p-2">{num(row.avg_r)}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
